/**
 * Where the admin bearer token lives, and who may do what.
 *
 * **`sessionStorage`, not `localStorage`.** The token is a credential to the
 * highest-privilege surface in the system, and the difference is what happens
 * when the operator walks away: a `sessionStorage` token dies with the tab,
 * while a `localStorage` one waits on a shared machine for whoever sits down
 * next. It survives a page refresh, which is the only reason not to keep it
 * purely in memory — a console that logged you out every time you hit reload is
 * a console people keep signed in on a second monitor instead.
 *
 * Neither option defends against XSS: script running on this origin can read
 * either one. What actually carries that weight is that this app renders no
 * HTML it did not write — player-supplied strings (references, notes) go
 * through React as text, and the one piece of genuinely untrusted content, the
 * proof image, is served by the API under `nosniff`, `Content-Disposition:
 * attachment` and `default-src 'none'`.
 */

const TOKEN_KEY = 'bingo.admin.token';
const EXPIRY_KEY = 'bingo.admin.expiresAt';
/** The trusted-device capability — deliberately localStorage (outlives tabs): it exists to identify the browser, not to expire with it. */
const TRUSTED_KEY = 'bingo.admin.trustedDevice';

export function setToken(token: string, expiresAt: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(EXPIRY_KEY, expiresAt);
}

/**
 * The trusted-device capability. Sent on every admin request; when valid it
 * lets this browser through the IP allowlist on any network (§7.6). A
 * capability, not a credential — login still requires password + TOTP.
 */
export function getTrustedDevice(): string | null {
  return localStorage.getItem(TRUSTED_KEY);
}

export function setTrustedDevice(token: string): void {
  localStorage.setItem(TRUSTED_KEY, token);
}

export function clearTrustedDevice(): void {
  localStorage.removeItem(TRUSTED_KEY);
}

/**
 * The token, or null if there is none or it has expired.
 *
 * Checking the expiry here rather than waiting for a 401 means a stale tab
 * shows the login screen instead of firing a doomed request at every endpoint
 * it loads. The server checks independently — this is a courtesy, and a clock
 * skewed the wrong way would only cost one round trip.
 */
export function getToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const expiresAt = sessionStorage.getItem(EXPIRY_KEY);
  if (!token || !expiresAt) return null;
  if (new Date(expiresAt).getTime() <= Date.now()) {
    clearToken();
    return null;
  }
  return token;
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(EXPIRY_KEY);
}

/**
 * Who may do what — re-exported from `@bingo/shared`, the one table this
 * console and the API both read.
 *
 * It used to be a hand-kept copy of the server's, and the copy is what broke
 * the Posters screen: the server granted `operator` `media_asset.manage` and
 * `system.notice`, the copy never gained either, and so an operator got a
 * poster list with no Edit, Replace or Delete anywhere on it — no error, no
 * 403, just absent controls. Sharing the table is what makes that
 * unrepresentable.
 *
 * **This is still cosmetics, and must never be the only thing standing between
 * an operator and an action.** Every admin route carries
 * `adminGuard(permission)`; this exists so a `finance` admin is not shown a
 * "Create round" button that answers 403, which teaches people to click
 * hopefully and ignore errors.
 */
export { can, PERMISSIONS } from '@bingo/shared';
